/**
 * WHICH BUSINESS IS THE APP SHOWING?
 *
 * The real backup carries TWO companies, and the user switches between them.
 * Everything the app shows — the document list, the letterhead, the GSTIN, the
 * bank details, the numbering series — must belong to exactly one of them at a
 * time. This file is the single answer to "which one", so no screen has to
 * decide for itself.
 *
 * WHY NOT "WHICHEVER ROW CAME BACK FIRST"
 * ---------------------------------------
 * `useDocuments` used to do `businesses.toArray()[0]`. With one company that is
 * fine; with two it is a coin toss decided by key ordering. If it picks wrong the
 * user is looking at one company's invoices under another company's name, with
 * no way to tell and no way to change it. That is the worst failure in this
 * category, so the choice is stored, validated, and owned by the user.
 *
 * THE RULES, AND WHY EACH ONE EXISTS
 * ----------------------------------
 *  - An unset preference reads as "no business", NOT as "the first one". A screen
 *    that quietly picks a business is the bug this file exists to remove.
 *  - Switching to a business that does not exist is REFUSED. A dangling pointer
 *    would leave every screen empty with no explanation.
 *  - A preference pointing at a deleted business reads as "no business" rather
 *    than throwing, because a re-import can retire a row out from under it.
 *  - The answer is validated against the `businesses` table on EVERY read, not
 *    just on write. Trusting a stored string is how a stale id becomes a crash.
 */
import { legacyBusinessId } from '../migration/legacyId';
import type { Table } from 'dexie';
import { z } from 'zod';
import type { Business, Setting } from '../db/types';

/** The setting row that names the business currently being worked in. */
export const ACTIVE_BUSINESS_SETTING_ID = 'app:activeBusinessId';
export const ACTIVE_BUSINESS_KEY = 'activeBusinessId';

/** The two tables needed to resolve the current business. */
export interface BusinessSessionTables {
  settings: Table<Setting, string>;
  businesses: Table<Business, string>;
}

/**
 * Switching to a business that is not in the database.
 *
 * Its own type so the caller can tell "you asked for something impossible" from
 * "the read failed", and refuse without corrupting the stored preference.
 */
export class UnknownBusinessError extends Error {
  constructor(readonly businessId: string) {
    super(
      `Cannot make "${businessId}" the current business: no such business exists. ` +
        'Writing it anyway would leave every screen empty with no explanation.',
    );
    this.name = 'UnknownBusinessError';
  }
}

/** No business has been chosen, or none exists. Not an error — a first-run state. */
export class NoActiveBusinessError extends Error {
  constructor(message = 'No business is selected yet. Import a backup or choose a company first.') {
    super(message);
    this.name = 'NoActiveBusinessError';
  }
}

/**
 * Which business should be active after importing this backup?
 *
 * Returns a LEGACY company id (`company_1`), not a 2.0 business id — the caller
 * maps it through the importer's own `legacyBusinessId`, because that mapping
 * lives with the code that creates the rows and duplicating the string here is
 * how the two drift apart and the app opens a company that does not exist.
 *
 * THE FALLBACK IS NOT OPTIONAL. An older or hand-edited backup may carry no
 * `activeCompanyId` — the sanitised one-invoice fixture does not. Returning
 * null there would mean the import succeeds, reports "Imported N documents", and
 * then shows an EMPTY list, because nothing selected a company. That is the
 * exact failure this whole thread exists to end, reintroduced through the back
 * door. So: the named company if there is one, otherwise the first company in
 * the backup's own key order, which is the order 1.6.2 wrote them.
 *
 * Pure, so the rule is testable without a database.
 */
export function activeCompanyFromBackup(backup: {
  readonly activeCompanyId?: string;
  readonly companies?: Record<string, unknown>;
}): string | null {
  const companies = backup.companies ?? {};
  const ids = Object.keys(companies);
  if (ids.length === 0) return null;

  // Honour the backup's own choice when it made one AND that company is really
  // present. A backup that names a company it does not contain is malformed, and
  // falling through to the first real one beats pointing at nothing.
  const named = backup.activeCompanyId;
  if (named !== undefined && named in companies) return named;

  return ids[0] ?? null;
}

/**
 * Every business, in a stable order.
 *
 * Sorted by legal name rather than left in whatever order the index returned, so
 * the switcher does not reshuffle itself between runs. An empty list on a fresh
 * install is a real state, not a failure.
 */
export async function listBusinesses(db: BusinessSessionTables): Promise<Business[]> {
  const rows = await db.businesses.toArray();
  return rows.sort((a, b) => a.legalName.localeCompare(b.legalName));
}

/**
 * The current business id, or null.
 *
 * Validated on every read: a stored value that no longer names a real business
 * is treated as no choice at all, because the alternative is a screen that
 * renders nothing and blames the user.
 */
export async function readActiveBusinessId(db: BusinessSessionTables): Promise<string | null> {
  const row = await db.settings.get(ACTIVE_BUSINESS_SETTING_ID);
  const id = z.string().min(1).safeParse(row?.value);
  if (!id.success) return null;
  // The pointer can outlive its target. A re-import or a deliberate removal
  // retires the row; the stored id must not become a dangling reference.
  const exists = await db.businesses.get(id.data);
  return exists ? id.data : null;
}

/**
 * Make `businessId` the current business.
 *
 * @throws UnknownBusinessError if that business does not exist. Refusing is the
 *         point: storing an id that resolves to nothing is how every screen ends
 *         up silently empty.
 */
export async function setActiveBusinessId(
  db: BusinessSessionTables,
  businessId: string,
): Promise<void> {
  const exists = await db.businesses.get(businessId);
  if (!exists) throw new UnknownBusinessError(businessId);
  await db.settings.put({
    id: ACTIVE_BUSINESS_SETTING_ID,
    scope: 'app',
    scopeId: 'app',
    key: ACTIVE_BUSINESS_KEY,
    value: businessId,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * The current business, in full.
 *
 * Callers that render a letterhead need the whole record — legal name, GSTIN,
 * state code, bank account — not just an id to filter by. The state code in
 * particular is per-business and decides INTRA vs INTER, so a hardcoded '36'
 * would be wrong the moment a second company sits in another state.
 *
 * @throws NoActiveBusinessError when nothing is selected.
 */
export async function resolveActiveBusiness(db: BusinessSessionTables): Promise<Business> {
  const id = await readActiveBusinessId(db);
  if (id === null) throw new NoActiveBusinessError();
  const business = await db.businesses.get(id);
  // readActiveBusinessId already proved this exists; re-checking would be
  // redundant, and returning a non-null from a nullable read is a lie.
  if (!business) throw new NoActiveBusinessError(`Business "${id}" vanished mid-read.`);
  return business;
}

/**
 * A raw 1.6.x company pointer, in the form the old backups stored.
 */
const LEGACY_COMPANY_POINTER = /^company_\d+$/;

/**
 * The importer's own business-id factory, kept here so the recovery logic never
 * drifts from the code that created the rows.
 */
export function resolveLegacyBusinessId(rawCompanyId: string): string {
  return legacyBusinessId(rawCompanyId);
}

/**
 * Extract a raw `company_N` pointer from a stored business id, if the stored id
 * is one of the old raw pointers.
 */
export function extractLegacyCompanyPointer(businessId: string): string | null {
  return LEGACY_COMPANY_POINTER.test(businessId) ? businessId : null;
}

/**
 * Recover a dangling 1.6.x pointer without touching the stored value unless a
 * valid target exists.
 *
 * Idempotent: after a successful heal, `readActiveBusinessId` resolves and the
 * pointer is never written again. If the mapped row does not exist (truly
 * orphaned), the stored value is left alone and the user gets a real error
 * downstream.
 */
export async function healActiveBusinessPointer(
  db: BusinessSessionTables,
): Promise<void> {
  // Read the RAW stored value — not through readActiveBusinessId, which
  // validates against the businesses table and returns null for a dangling
  // legacy pointer, which is exactly the case we need to heal.
  const row = await db.settings.get(ACTIVE_BUSINESS_SETTING_ID);
  const storedId = z.string().min(1).safeParse(row?.value).data;
  if (!storedId) return; // nothing stored

  // Already resolves — convergence is guaranteed after the first successful run.
  if (await db.businesses.get(storedId) !== undefined) return;

  // Only raw `company_N` pointers can be healed.
  if (!LEGACY_COMPANY_POINTER.test(storedId)) return;

  const mapped = legacyBusinessId(storedId);
  const target = await db.businesses.get(mapped);
  if (target !== undefined) {
    await setActiveBusinessId(db, mapped);
  }
}
